/**
 * Login Form Component
 *
 * A clean, accessible login form with:
 * - React Hook Form for form management
 * - Zod validation with real-time feedback
 * - Loading states and error handling
 * - Keyboard navigation support
 * - Development logging
 */

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Mail, Lock } from 'lucide-react'
import { API_BASE_URL } from '../../utils/constants'
import { Link } from 'react-router-dom'

import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card'
import { useAuth } from '../../hooks/useAuth'
import { loginSchema, type LoginFormData } from '../../utils/validation'
import { logger, loggerUtils } from '../../utils/logger'
import { cn } from '../../utils/cn'
import { getUserFriendlyErrorMessage, getErrorField } from '../../utils/errorMessages'

/**
 * Props for the LoginForm component
 */
interface LoginFormProps {
  /** Callback fired when login is successful */
  onSuccess?: () => void
  /** Callback fired when login fails */
  onError?: (error: string) => void
  /** Additional CSS classes */
  className?: string
}

/**
 * LoginForm Component
 *
 * Handles user authentication with comprehensive error handling and validation
 */
export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onError,
  className
}) => {
  // ====================================
  // HOOKS & STATE
  // ====================================

  const { login, isLoggingIn, error: authError } = useAuth()

  // Local state for password visibility
  const [showPassword, setShowPassword] = useState(false)

  // Form setup with React Hook Form and Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
    setError,
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur', // Validate on blur for better UX
    defaultValues: {
      email: '',
      password: ''
    }
  })

  // ====================================
  // EVENT HANDLERS
  // ====================================

  /**
   * Handles form submission
   */
  const onSubmit = async (data: LoginFormData) => {
    loggerUtils.functionEntry('LoginForm.onSubmit', { email: data.email })

    try {
      // Clear any previous errors
      clearErrors()

      logger.auth('Attempting login', { email: data.email })

      // Attempt login - React Query handles success/error automatically
      login(data, {
        onSuccess: () => {
          logger.auth('Login successful', { email: data.email })
          loggerUtils.userAction('login_success', 'login_form')
          onSuccess?.()
        },
        onError: (error: any) => {
          // Convert to user-friendly message
          const friendlyMessage = getUserFriendlyErrorMessage(error)
          const errorField = getErrorField(error)

          logger.error('Login failed', {
            component: 'LoginForm',
            error: friendlyMessage,
            originalError: error,
            data: { email: data.email }
          })

          // Set field-specific errors
          if (errorField === 'email') {
            setError('email', { message: friendlyMessage })
          } else if (errorField === 'password') {
            setError('password', { message: friendlyMessage })
          }
          // For general errors, React Query's error will be displayed globally

          onError?.(friendlyMessage)
          loggerUtils.userAction('login_failed', 'login_form', { error: friendlyMessage })
        }
      })

    } catch (error) {
      // This shouldn't happen with React Query, but just in case
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      logger.error('Unexpected login error', {
        component: 'LoginForm',
        error: errorMessage
      })
    }

    loggerUtils.functionExit('LoginForm.onSubmit')
  }

  /**
   * Toggles password visibility
   */
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
    loggerUtils.userAction('toggle_password_visibility', 'password_input', { visible: !showPassword })
  }

  /**
   * Handles input focus events for logging
   */
  const handleInputFocus = (fieldName: string) => {
    loggerUtils.userAction('focus_input', fieldName)
  }

  // ====================================
  // COMPUTED VALUES
  // ====================================

  const isFormDisabled = isLoggingIn || isSubmitting
  const hasValidationErrors = Object.keys(errors).length > 0

  // Convert auth error to friendly message
  const authErrorMessage = authError ? getUserFriendlyErrorMessage(authError) : ''
  const displayError = authErrorMessage || (hasValidationErrors ? 'Please fix the errors below' : '')

  // Log form state changes in development
  React.useEffect(() => {
    if (import.meta.env.VITE_NODE_ENV === 'development') {
      logger.debug('LoginForm state changed', {
        component: 'LoginForm',
        data: {
          hasErrors: hasValidationErrors,
          isDirty,
          isValid,
          isLoggingIn,
          isSubmitting
        }
      })
    }
  }, [hasValidationErrors, isDirty, isValid, isLoggingIn, isSubmitting])

  // ====================================
  // RENDER
  // ====================================

  return (
    <Card className={cn('w-full max-w-md mx-auto', className)}>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-foreground">
          Welcome back
        </CardTitle>
        <CardDescription>
          Sign in to your CleverAct AI account to continue
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {/* Global Error Display */}
          {displayError && (
            <div
              className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg"
              role="alert"
              aria-live="polite"
            >
              {displayError}
            </div>
          )}

          {/* Email Field */}
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground"
            >
              Email address
            </label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                autoComplete="email"
                disabled={isFormDisabled}
                error={errors.email?.message}
                startIcon={<Mail className="h-4 w-4" />}
                placeholder="Enter your email"
                {...register('email')}
                onFocus={() => handleInputFocus('email')}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-foreground"
            >
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                disabled={isFormDisabled}
                error={errors.password?.message}
                startIcon={<Lock className="h-4 w-4" />}
                endIcon={
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="text-muted-foreground hover:text-foreground focus:outline-none focus:text-foreground"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1} // Remove from tab order since it's decorative
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
                placeholder="Enter your password"
                {...register('password')}
                onFocus={() => handleInputFocus('password')}
                aria-describedby={errors.password ? 'password-error' : undefined}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="space-y-4">
            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full rounded-full bg-gradient-to-r from-primary to-emerald-500 text-white"
              loading={isFormDisabled}
              disabled={isFormDisabled}
            >
              {isFormDisabled ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>

          {/* Forgot Password Link */}
          <div className="text-center">
            <Link
              to="/auth/forgot-password"
              className="text-sm text-primary hover:text-primary/80 focus:outline-none focus:underline"
              onClick={() => {
                logger.info('Forgot password clicked')
                loggerUtils.userAction('forgot_password_click', 'forgot_password_link')
              }}
            >
              Forgot your password?
            </Link>
          </div>
        </form>

        {/* OAuth Social Login */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {/* Google Login */}
            <button
              type="button"
              onClick={() => {
                logger.info('Google OAuth login initiated')
                loggerUtils.userAction('oauth_login_click', 'google_button')
                window.location.href = `${API_BASE_URL}/auth/oauth/login/google`
              }}
              className="w-full flex items-center justify-center px-4 py-2 border border-border rounded-lg shadow-sm bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* GitHub Login */}
            <button
              type="button"
              onClick={() => {
                logger.info('GitHub OAuth login initiated')
                loggerUtils.userAction('oauth_login_click', 'github_button')
                window.location.href = `${API_BASE_URL}/auth/oauth/login/github`
              }}
              className="w-full flex items-center justify-center px-4 py-2 border border-border rounded-lg shadow-sm bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"/>
              </svg>
              Continue with GitHub
            </button>
          </div>
        </div>

        {/* Sign Up Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link
              to="/auth/register"
              className="font-medium text-primary hover:text-primary/80 focus:outline-none focus:underline"
              onClick={() => loggerUtils.userAction('navigate_to_register', 'register_link')}
            >
              Sign up — coming soon
            </Link>
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export default LoginForm
